import { Box, styled } from '@mui/material';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { RenderUnsettledUI } from 'shared/components';
import { useSiteDetection } from 'shared/hooks';
import { AddBlockButton } from './enroll-blocks/add-block-button/add-block-button';
import { OrBlock } from './enroll-blocks/or-block/or-block';
import { EnrollNodeEditorProps, EnrollNodeFormValues } from './enroll-node-editor.types';
import { useGetTriggerData } from './hooks/use-get-trigger-data';
import { WorkflowSidebarFooter } from '../common/workflow-sidebar-footer/workflow-sidebar-footer';

const defaultOrBlocks: EnrollNodeFormValues['orBlocks'] = [
  { andBlocks: [{ condition: 'equal', entityType: 'branchOfficeIds' as const, entityId: [] }] },
];

function normalizeOrBlocksForForm(
  source: Partial<EnrollNodeFormValues> | undefined
): EnrollNodeFormValues['orBlocks'] {
  const blocks = source?.orBlocks;
  if (!blocks?.length) {
    return defaultOrBlocks;
  }
  return blocks.map((or) => ({
    ...or,
    andBlocks: or.andBlocks.map((and) => ({
      ...and,
      entityId: and.entityId.map((id) => ({ label: id.label, value: id.value })),
    })),
  }));
}

export const EnrollNodeEditor = ({ node, onSave, onClose }: EnrollNodeEditorProps) => {
  const { site } = useSiteDetection();
  const enrollDataQuery = useGetTriggerData(site);
  const source = (node.data ?? {}) as Partial<EnrollNodeFormValues>;

  const form = useForm<EnrollNodeFormValues>({
    defaultValues: {
      orBlocks: normalizeOrBlocksForForm(source),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'orBlocks',
  });

  if (enrollDataQuery.status !== 'success') {
    return <RenderUnsettledUI data={enrollDataQuery} />;
  }

  const submit = form.handleSubmit((values: EnrollNodeFormValues) => {
    onSave({ ...(node.data ?? {}), ...values });
    onClose();
  });

  return (
    <FormProvider {...form}>
      <EditorWrapper>
        <Title>Select one or multiple enroll conditions for your workflow</Title>
        <BlocksWrapper>
          {fields.map((f, index) => (
            <OrBlock
              key={f.id}
              index={index}
              canRemove={fields.length > 1}
              onRemove={() => remove(index)}
              entityOptions={enrollDataQuery.data}
            />
          ))}
          <AddBlockButton
            onClick={() =>
              append({
                andBlocks: [{ condition: 'equal', entityId: [], entityType: 'branchOfficeIds' }],
              })
            }
            label="OR"
          />
        </BlocksWrapper>

        <WorkflowSidebarFooter onClose={onClose} submit={submit} />
      </EditorWrapper>
    </FormProvider>
  );
};

const EditorWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const Title = styled(Box)({
  fontWeight: 700,
  fontSize: 18,
  textAlign: 'center',
  marginBottom: 12,
  marginTop: 12,
});

const BlocksWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  flex: 1,
  overflowY: 'auto',
  padding: '12px 4px',
});
